/**
 * dashboard/students/index.tsx — N-27 Tutor aggregated student roster.
 *
 * Lives at `/dashboard/students`. Lists all students enrolled in any
 * class the calling tutor owns, plus filter controls (search by name,
 * filter by class, status filter) and a link from the existing
 * tutor sidebar Siswa entry.
 */

import { createFileRoute, Link, redirect, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import type { ReactNode } from 'react';
import { PageHeader } from '../../../components/ui/PageHeader.js';
import { StatePanel } from '../../../components/ui/StatePanel.js';
import { StudentsRoster } from '../../../components/tutor/StudentsRoster.js';
import {
  freshSessionQuery,
  tutorStudentsQuery,
} from '../../../lib/api/queries.js';

const optionalSearchString = z.preprocess(
  (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
  z.string().optional(),
);

export const studentsRosterSearchSchema = z.object({
  q: optionalSearchString,
  classId: optionalSearchString,
  status: z.preprocess(
    (value) => (typeof value === 'string' && value.trim() === '' ? undefined : value),
    z.enum(['active', 'inactive']).optional(),
  ),
});

export const Route = createFileRoute('/dashboard/students/')({
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role === 'admin') {
      throw redirect({ to: '/admin' });
    }
    if (user.role === 'parent') {
      throw redirect({ to: '/parent' });
    }
    if (user.role !== 'tutor' || !user.isApprovedTutor) {
      throw redirect({ to: '/me' });
    }
    return null;
  },
  validateSearch: (search) => studentsRosterSearchSchema.parse(search),
  component: StudentsRosterPage,
});

function StudentsRosterPage(): ReactNode {
  const rawSearch = useSearch({ from: '/dashboard/students/' });
  // validateSearch output isn't fully inferred through this route's
  // generated interface; narrow with a defensive shape (extra fields
  // become undefined and helpers below normalize to '').
  const search = rawSearch as { q?: string; classId?: string; status?: string };
  const q = search.q ?? '';
  const classId = search.classId ?? '';
  const status = search.status ?? '';
  const roster = useQuery(tutorStudentsQuery(search));
  const list = roster.data?.ok ? roster.data.data : null;

  return (
    <main className="page-shell" data-testid="students-roster-page">
      <PageHeader
        eyebrow="Daftar siswa"
        title="Siswa kamu"
        description="Semua siswa yang terdaftar di kelas yang kamu ajar — lintas kelas dalam satu tampilan."
      />

      <section className="section students-roster-filters" aria-label="Filter daftar siswa">
        <form className="students-roster-filter-form" data-testid="students-roster-filter-form">
          <label className="form-field">
            <span>Cari nama</span>
            <input
              type="search"
              name="q"
              defaultValue={q}
              placeholder="Contoh: Alya"
              data-testid="students-roster-filter-q"
            />
          </label>
          <label className="form-field">
            <span>Kelas</span>
            <select
              name="classId"
              defaultValue={classId}
              data-testid="students-roster-filter-class"
            >
              <option value="">Semua kelas</option>
              {list?.classes.map((c) => (
                <option key={c.classId} value={c.classId}>
                  {c.className} ({c.studentCount} siswa)
                </option>
              ))}
            </select>
          </label>
          <label className="form-field">
            <span>Status</span>
            <select
              name="status"
              defaultValue={status}
              data-testid="students-roster-filter-status"
            >
              <option value="">Semua</option>
              <option value="active">Aktif (≤ 7 hari)</option>
              <option value="inactive">Tidak aktif</option>
            </select>
          </label>
          <div className="cluster">
            <button type="submit" className="button button-primary">
              Terapkan
            </button>
            {(q || classId || status) && (
              <Link to="/dashboard/students" className="button button-ghost">
                Reset
              </Link>
            )}
          </div>
        </form>
      </section>

      <section className="section" aria-label="Daftar siswa">
        {roster.isLoading ? (
          <p className="form-message">Memuat daftar siswa…</p>
        ) : !list ? (
          <StatePanel
            variant="error"
            title="Gagal memuat daftar siswa"
            description="Coba segarkan halaman ini. Jika tetap gagal, hubungi admin."
          />
        ) : (
          <>
            <p className="students-roster-count" data-testid="students-roster-count">
              Menampilkan <strong>{list.students.length}</strong> dari{' '}
              <strong>{list.totalCount}</strong> siswa
              {q ? ` (cari "${q}")` : ''}.
            </p>
            <StudentsRoster
              students={list.students}
              classes={list.classes}
              totalCount={list.totalCount}
              tutorHasClasses={list.tutorHasClasses}
            />
          </>
        )}
      </section>
    </main>
  );
}
